/* Gratisprobe (Stufe 2): Auswahl-Drawer auf der Produktseite und
 * Warenkorb-Regeln auf jeder Seite.
 *
 * Zwei Teile in einer Datei, weil sie dasselbe Datenmodell teilen:
 *
 * 1. REGELN (laufen ueberall): genau eine Gratisprobe je Bestellung, und nur
 *    mit qualifizierender Vollflasche. Haengt am cartUpdate-Ereignis des
 *    Themes -- das feuert aus product-form, quick-add, cart und
 *    quick-order-list, deckt also alle Wege ab (Spez §3.4 Regel 8).
 *    Der Hub prueft dieselben Regeln serverseitig noch einmal am Webhook;
 *    dieses Skript ist die Kundensicht, der Hub der Zaun.
 *
 * 2. AUSWAHL (nur Produktseite): Drawer mit maximal sechs Proben,
 *    "Ueberrascht mich" und "Keine Probe". Eine konkrete Probe wird nach dem
 *    Kauf der Flasche als eigene CHF-0-Zeile in den Warenkorb gelegt (echte
 *    SKU, echter Bestand); "Ueberrascht mich" bleibt eine Zeileneigenschaft
 *    an der Flasche -- die konkrete SKU teilt der Hub vor dem Fulfillment zu,
 *    damit nie eine Probe reserviert wird, deren Bestand null ist.
 *
 * Erkennung im Warenkorb ausschliesslich ueber das SKU-Praefix PROBE- --
 * dieselbe Regel wie in server/src/proben.js. Wo kein Probenprodukt existiert
 * (BASE, GB), rendert das Snippet die alte Select-Mechanik und dieses Skript
 * laeuft leer durch.
 */
(() => {
  const PROBE_PRAEFIX = 'PROBE-';

  /* Ereignis an die Web-Pixel-API melden (Stufe 2, Spez §6.1).
   *
   * Bewusst NICHT gtag/fbq direkt aus dem Theme: der GA4-Custom-Pixel haelt
   * schon ein Einwilligungs-Gate, und ein zweiter Sender im Theme braeuchte ein
   * zweites. Ueber publish erbt jedes Ereignis das vorhandene Gate.
   * Personenbezogenes gehoert nie hinein -- nur Kennungen und Zahlen. */
  const melde = (name, daten) => {
    try { window.Shopify?.analytics?.publish?.(name, daten || {}); } catch { /* Messung darf den Kauf nie stoeren */ }
  };
  const sprache = () => document.documentElement.lang || '';
  const IST_FLASCHE = (item) =>
    /Scent Nr\.\s*\d+/.test(item.product_title || item.title || '') &&
    !(item.sku || '').startsWith(PROBE_PRAEFIX) && item.price > 0;
  const IST_PROBE = (item) => (item.sku || '').startsWith(PROBE_PRAEFIX);

  const holeCart = () => fetch(`${window.Shopify?.routes?.root || '/'}cart.js`).then((r) => r.json());
  const post = (pfad, body) =>
    fetch(`${window.Shopify?.routes?.root || '/'}${pfad}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    }).then((r) => r.json());

  /* ---- Teil 1: Regeln ----------------------------------------------------
   * Nach jeder Warenkorb-Aenderung: Proben ohne Flasche entfernen; mehr als
   * eine Probe auf die zuletzt gewaehlte eindampfen (cart.js listet neueste
   * zuerst). `laeuft` verhindert, dass unsere eigene Korrektur eine zweite
   * Runde anstoesst. */
  let laeuft = false;
  async function regelnDurchsetzen() {
    if (laeuft) return;
    laeuft = true;
    try {
      const cart = await holeCart();
      const proben = cart.items.filter(IST_PROBE);
      if (!proben.length) return;
      const flaschen = cart.items.some(IST_FLASCHE);
      const updates = {};
      if (!flaschen) {
        for (const p of proben) updates[p.key] = 0;               // Regel 4/5: keine Flasche -> keine Probe
      } else {
        proben.slice(1).forEach((p) => { updates[p.key] = 0; });  // Regel 1/3: nur die neueste bleibt
        if (proben[0].quantity > 1) updates[proben[0].key] = 1;   // Menge einer Probe ist immer 1
      }
      if (Object.keys(updates).length) {
        const antwort = await post('cart/update.js', {
          updates,
          sections: sektionsListe(),
        });
        sektionenEinsetzen(antwort);
      }
    } catch (e) {
      console.warn('gratisprobe: Regelpruefung fehlgeschlagen', e);
    } finally {
      laeuft = false;
    }
  }

  function sektionsListe() {
    const cart = document.querySelector('cart-drawer') || document.querySelector('cart-items');
    return cart?.getSectionsToRender?.().map((s) => s.section || s.id).filter(Boolean) || [];
  }
  function sektionenEinsetzen(antwort) {
    const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
    if (antwort?.sections && cart?.renderContents) {
      try { cart.renderContents({ ...antwort, id: antwort.items?.[0]?.variant_id }); } catch { /* Anzeige holt sich beim naechsten Oeffnen den Stand */ }
    }
  }

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, () => regelnDurchsetzen());
  }
  document.addEventListener('DOMContentLoaded', () => regelnDurchsetzen());

  /* ---- Teil 2: Auswahl auf der Produktseite ------------------------------ */
  class GratisprobeWahl extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      this.feld = this.querySelector('[data-probe-property]');
      this.stand = this.querySelector('[data-probe-stand]');
      this.variante = null;   // Varianten-ID der konkreten Probe, null sonst

      this.produktId = this.closest('[data-product-id]')?.dataset.productId || '';
      melde('sample_selector_view', { product_id: this.produktId, locale: sprache(), has_sample: false });
      this.querySelector('[data-probe-oeffnen]')?.addEventListener('click', () => {
        this.dialog.showModal();
        melde('sample_selector_open', { product_id: this.produktId, locale: sprache() });
      });
      this.querySelector('[data-probe-schliessen]')?.addEventListener('click', () => this.dialog.close());
      this.dialog?.addEventListener('click', (e) => {
        if (e.target === this.dialog) this.dialog.close();       // Klick auf den Hintergrund
      });
      this.querySelectorAll('input[name="gratisprobe"]').forEach((r) =>
        r.addEventListener('change', () => this.gewaehlt(r)));

      // Nach erfolgreichem Add-to-Cart DIESER Sektion die Probe nachlegen.
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.abo = subscribe(PUB_SUB_EVENTS.cartUpdate, (d) => {
          if (d?.source !== 'product-form' || !this.variante) return;
          if (!this.closest('section')?.contains(document.activeElement) &&
              !this.closest('.product')) { /* fremde Sektion */ }
          this.probeNachlegen();
        });
      }
    }
    disconnectedCallback() { this.abo?.(); }

    gewaehlt(radio) {
      const art = radio.dataset.art;                              // keine | ueberrascht | probe
      this.variante = art === 'probe' ? radio.value : null;
      // Zeileneigenschaft an der Flasche: fuer das Backoffice und -- bei
      // "Ueberrascht mich" -- als Auftrag an den Hub. Werte bewusst deutsch
      // und je Markt fest (siehe Kommentar im Snippet zur Bestellhistorie).
      this.feld.value = art === 'ueberrascht' ? this.feld.dataset.wertUeberrascht
        : art === 'keine' ? this.feld.dataset.wertKeine
        : radio.dataset.name;
      this.stand.textContent = art === 'keine' ? '' : `${this.stand.dataset.prefix} ${radio.dataset.label}`;
      this.stand.hidden = art === 'keine';
      if (art === 'keine') {
          // has_sample verlangt Punkt 10: ohne das Feld laesst sich in GA4 nicht
        // trennen, welche Bestellungen mit Probe liefen und welche ohne.
        melde('sample_removed', { product_id: this.produktId, sample_id: '', has_sample: false, locale: sprache() });
      } else {
        melde('sample_selected', { product_id: this.produktId,
          sample_id: art === 'probe' ? String(radio.value) : '',
          selection_mode: art === 'probe' ? 'manual' : 'surprise',
          has_sample: true, locale: sprache() });
      }
      this.dialog.close();
    }

    async probeNachlegen() {
      const id = this.variante;
      this.variante = null;   // genau einmal je Kauf; neue Auswahl setzt neu
      try {
        const antwort = await post('cart/add.js', {
          items: [{ id: Number(id), quantity: 1, properties: { _typ: 'gratisprobe' } }],
          sections: sektionsListe(),
        });
        if (antwort.status && antwort.status !== 200) throw new Error(antwort.description || antwort.message);
        sektionenEinsetzen(antwort);
        await regelnDurchsetzen();   // ersetzt eine evtl. schon liegende Probe
      } catch (e) {
        melde('sample_unavailable', { sample_id: String(id), cart_state: 'add_failed', has_sample: false, locale: sprache() });
        console.warn('gratisprobe: Probe konnte nicht beigelegt werden', e);
      }
    }
  }
  if (!customElements.get('gratisprobe-wahl')) customElements.define('gratisprobe-wahl', GratisprobeWahl);
})();
